import React, { useEffect, useRef, useState } from 'react';
import {
	Autocomplete,
	Badge,
	Box,
	Button,
	Chip,
	Collapse,
	Divider,
	Grid,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import {
	Clear as ClearIcon,
	Close as CloseIcon,
	ExpandLess as ExpandLessIcon,
	ExpandMore as ExpandMoreIcon,
	FilterList as FilterListIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { IBranchOfficesList } from '../../types';
import { getBranchOfficesList, getCoinsList } from '../../services';
import { ICoin } from 'features/payment/types';
import {
	CIVIL_DATE_PRESETS,
	CIVIL_DATE_PRESET_LABELS,
	CivilDatePreset,
	formatDateRangeLabel,
	formatDateYmd,
	getDateRangeFromPreset,
} from 'features/common/helpers/datePresets';

// ─── Tipos ───────────────────────────────────────────────────────────────────

export interface CashTransactionFiltersValue {
	branchOfficeId?: number;
	coinId?: number;
	crmTicket?: number;
	createdAtFrom?: string;
	createdAtTo?: string;
	createdBy?: string;
}

interface LocalFilters {
	branchOffice: IBranchOfficesList | null;
	coin: ICoin | null;
	crmTicket: string;
	createdAtFrom: Date | null;
	createdAtTo: Date | null;
	createdBy: string;
}

const defaultFilters: LocalFilters = {
	branchOffice: null,
	coin: null,
	crmTicket: '',
	createdAtFrom: null,
	createdAtTo: null,
	createdBy: '',
};

const FILTER_DEBOUNCE_MS = 500;

// ─── Barra de presets de fecha ────────────────────────────────────────────────

interface DateBarProps {
	activePreset: CivilDatePreset | '';
	onPresetToggle: (p: CivilDatePreset) => void;
}

const DateBar = ({ activePreset, onPresetToggle }: DateBarProps): JSX.Element => (
	<Paper
		elevation={0}
		sx={{
			p: 1.5,
			mb: 2,
			borderRadius: 2,
			border: '1px solid rgba(0,0,0,0.08)',
			background: 'linear-gradient(180deg,#fafbff 0%,#ffffff 100%)',
		}}
	>
		<Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
			<Typography variant="caption" color="text.secondary" sx={{ minWidth: 64, fontWeight: 600 }}>
				Creación
			</Typography>
			{CIVIL_DATE_PRESETS.map((preset) => {
				const range = getDateRangeFromPreset(preset);
				const isActive = activePreset === preset;
				return (
					<Tooltip
						key={preset}
						title={
							isActive
								? `${formatDateRangeLabel(range.from, range.to)} — Clic para quitar`
								: formatDateRangeLabel(range.from, range.to)
						}
					>
						<Box component="span" sx={{ position: 'relative', display: 'inline-flex' }}>
							<Button
								size="small"
								variant={isActive ? 'contained' : 'outlined'}
								onClick={() => onPresetToggle(preset)}
								sx={{ minWidth: 'auto', px: 1.25, py: 0.35, fontSize: '0.8125rem', textTransform: 'none' }}
							>
								{CIVIL_DATE_PRESET_LABELS[preset]}
							</Button>
							{isActive && (
								<CloseIcon
									sx={{
										position: 'absolute',
										top: -7,
										right: -7,
										fontSize: 13,
										bgcolor: 'background.paper',
										borderRadius: '50%',
										border: '1px solid',
										borderColor: 'primary.main',
										color: 'primary.main',
										p: '1px',
										pointerEvents: 'none',
										zIndex: 1,
									}}
								/>
							)}
						</Box>
					</Tooltip>
				);
			})}
		</Box>
	</Paper>
);

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
	onFilter: (filters: CashTransactionFiltersValue) => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export const CashTransactionFilters = ({ onFilter }: Props): JSX.Element => {
	const [filters, setFilters] = useState<LocalFilters>(defaultFilters);
	const [datePreset, setDatePreset] = useState<CivilDatePreset | ''>('');
	const [expanded, setExpanded] = useState(false);

	// Datos auxiliares — carga propia
	const [branchOffices, setBranchOffices] = useState<IBranchOfficesList[]>([]);
	const [coins, setCoins] = useState<ICoin[]>([]);

	const prevRef = useRef<string>('');
	const onFilterRef = useRef(onFilter);
	const crmDebounceRef = useRef<ReturnType<typeof setTimeout>>();
	const createdByDebounceRef = useRef<ReturnType<typeof setTimeout>>();

	// Cargar opciones al montar
	useEffect(() => {
		getBranchOfficesList()
			.then((r) => setBranchOffices(r.data))
			.catch(() => {
				/* silencioso */
			});
		getCoinsList()
			.then((r) => setCoins(r.data))
			.catch(() => {
				/* silencioso */
			});
	}, []);

	useEffect(() => {
		onFilterRef.current = onFilter;
	}, [onFilter]);

	// Emitir filtros cuando cambia el estado local
	useEffect(() => {
		const out: CashTransactionFiltersValue = {};
		if (filters.branchOffice) out.branchOfficeId = filters.branchOffice.id;
		if (filters.coin) out.coinId = filters.coin.id;
		if (filters.crmTicket.trim()) {
			const n = parseInt(filters.crmTicket.trim(), 10);
			if (!isNaN(n)) out.crmTicket = n;
		}
		if (filters.createdAtFrom) out.createdAtFrom = formatDateYmd(filters.createdAtFrom);
		if (filters.createdAtTo) out.createdAtTo = formatDateYmd(filters.createdAtTo);
		if (filters.createdBy.trim()) out.createdBy = filters.createdBy.trim();

		const str = JSON.stringify(out);
		if (prevRef.current !== str) {
			prevRef.current = str;
			onFilterRef.current(out);
		}
	}, [filters]);

	useEffect(
		() => () => {
			if (crmDebounceRef.current) clearTimeout(crmDebounceRef.current);
			if (createdByDebounceRef.current) clearTimeout(createdByDebounceRef.current);
		},
		[],
	);

	const set = <K extends keyof LocalFilters>(field: K, value: LocalFilters[K]) =>
		setFilters((prev) => ({ ...prev, [field]: value }));

	const handleDatePresetToggle = (preset: CivilDatePreset) => {
		if (datePreset === preset) {
			setDatePreset('');
			setFilters((prev) => ({ ...prev, createdAtFrom: null, createdAtTo: null }));
			return;
		}
		const { from, to } = getDateRangeFromPreset(preset);
		setDatePreset(preset);
		setFilters((prev) => ({ ...prev, createdAtFrom: from, createdAtTo: to }));
	};

	const handleDateChange = (field: 'createdAtFrom' | 'createdAtTo', value: Date | null) => {
		setDatePreset('');
		set(field, value);
	};

	const handleCrmTicketChange = (value: string) => {
		if (crmDebounceRef.current) clearTimeout(crmDebounceRef.current);
		crmDebounceRef.current = setTimeout(
			() => setFilters((prev) => ({ ...prev, crmTicket: value })),
			FILTER_DEBOUNCE_MS,
		);
	};

	const handleCreatedByChange = (value: string) => {
		if (createdByDebounceRef.current) clearTimeout(createdByDebounceRef.current);
		createdByDebounceRef.current = setTimeout(
			() => setFilters((prev) => ({ ...prev, createdBy: value })),
			FILTER_DEBOUNCE_MS,
		);
	};

	const handleClear = () => {
		setFilters(defaultFilters);
		setDatePreset('');
		setExpanded(false);
		onFilter({});
	};

	const activeCount = [
		!!filters.branchOffice,
		!!filters.coin,
		!!filters.crmTicket.trim(),
		!!filters.createdAtFrom,
		!!filters.createdAtTo,
		!!filters.createdBy.trim(),
	].filter(Boolean).length;

	// ── Render ────────────────────────────────────────────────────────────────

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<DateBar activePreset={datePreset} onPresetToggle={handleDatePresetToggle} />

			<Paper elevation={2} sx={{ p: 2, mb: 2 }}>
				{/* ── Header ── */}
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
					<Box display="flex" alignItems="center" gap={1} flexShrink={0}>
						<Badge badgeContent={activeCount} color="primary">
							<FilterListIcon color="action" />
						</Badge>
						<Typography variant="subtitle1" fontWeight={500}>
							Filtros:
						</Typography>
					</Box>

					{/* ── Chips de filtros activos ── */}
					{activeCount > 0 && (
						<Box display="flex" flexWrap="wrap" gap={0.5} flex={1}>
							{filters.branchOffice && (
								<Chip
									label={`Sucursal: ${filters.branchOffice.name}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('branchOffice', null)}
								/>
							)}
							{filters.coin && (
								<Chip
									label={`Moneda: ${filters.coin.code}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('coin', null)}
								/>
							)}
							{filters.crmTicket.trim() && (
								<Chip
									label={`Deal: ${filters.crmTicket.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('crmTicket', '')}
								/>
							)}
							{datePreset ? (
								<Chip
									label={`Creación: ${CIVIL_DATE_PRESET_LABELS[datePreset]}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => handleDatePresetToggle(datePreset)}
								/>
							) : (
								<>
									{filters.createdAtFrom && (
										<Chip
											label={`Desde: ${format(filters.createdAtFrom, 'dd/MM/yy')}`}
											size="small"
											color="primary"
											variant="outlined"
											onDelete={() => handleDateChange('createdAtFrom', null)}
										/>
									)}
									{filters.createdAtTo && (
										<Chip
											label={`Hasta: ${format(filters.createdAtTo, 'dd/MM/yy')}`}
											size="small"
											color="primary"
											variant="outlined"
											onDelete={() => handleDateChange('createdAtTo', null)}
										/>
									)}
								</>
							)}
							{filters.createdBy.trim() && (
								<Chip
									label={`Por: ${filters.createdBy.trim()}`}
									size="small"
									color="primary"
									variant="outlined"
									onDelete={() => set('createdBy', '')}
								/>
							)}
						</Box>
					)}

					<Box display="flex" gap={1}>
						<Button size="small" startIcon={<ClearIcon />} onClick={handleClear} variant="outlined" color="error">
							Limpiar
						</Button>
						<Button
							size="small"
							endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
							onClick={() => setExpanded((p) => !p)}
							variant="outlined"
							color="primary"
						>
							{expanded ? 'Menos filtros' : 'Más filtros'}
						</Button>
					</Box>
				</Box>

				{/* ── Filtros principales ── */}
				<Grid container spacing={1.5}>
					<Grid item xs={12} sm={6} md={3}>
						<Autocomplete
							options={branchOffices}
							value={filters.branchOffice}
							getOptionLabel={(o) => o.name}
							isOptionEqualToValue={(o, v) => o.id === v.id}
							onChange={(_, val) => set('branchOffice', val)}
							noOptionsText={branchOffices.length === 0 ? 'Cargando...' : 'Sin opciones'}
							renderInput={(params) => <TextField {...params} label="Sucursal" size="small" />}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<Autocomplete
							options={coins}
							value={filters.coin}
							getOptionLabel={(o) => o.code}
							isOptionEqualToValue={(o, v) => o.id === v.id}
							onChange={(_, val) => set('coin', val)}
							noOptionsText={coins.length === 0 ? 'Cargando...' : 'Sin opciones'}
							renderInput={(params) => <TextField {...params} label="Moneda" size="small" />}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<TextField
							fullWidth
							size="small"
							label="Deal o Caso"
							placeholder="ID numérico"
							onChange={(e) => handleCrmTicketChange(e.target.value)}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Creación desde"
							value={filters.createdAtFrom}
							onChange={(d) => handleDateChange('createdAtFrom', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={2}>
						<DatePicker
							label="Creación hasta"
							value={filters.createdAtTo}
							onChange={(d) => handleDateChange('createdAtTo', d)}
							inputFormat="dd/MM/yyyy"
							renderInput={(params) => <TextField {...params} size="small" fullWidth />}
						/>
					</Grid>
				</Grid>

				{/* ── Filtros avanzados ── */}
				<Collapse in={expanded}>
					<Divider sx={{ my: 1.5 }} />
					<Grid container spacing={1.5}>
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								size="small"
								label="Creado por"
								placeholder="Nombre o email"
								onChange={(e) => handleCreatedByChange(e.target.value)}
							/>
						</Grid>
					</Grid>
				</Collapse>
			</Paper>
		</LocalizationProvider>
	);
};
